// Lo que se enchufa a una estación (el módulo LoRa). Clima, barómetro, lluvia
// y viento son únicos; sondas de suelo y válvulas pueden ser varias (canal 1, 2...).
// Lo que tiene enchufado define qué se muestra: tarjetas del home, alertas
// posibles y riego. Ver backend/src/domain/entities/Component.ts.
export type ComponentKind = 'air' | 'pressure' | 'rain' | 'wind' | 'soil' | 'valve';
export type ComponentSource = 'manual' | 'auto';

export const COMPONENT_KINDS: ComponentKind[] = ['air', 'pressure', 'rain', 'wind', 'soil', 'valve'];
export const MULTI_CHANNEL_KINDS: ComponentKind[] = ['soil', 'valve'];
export const MAX_CHANNEL = 16;

export interface DeviceComponent {
  id: string;
  kind: ComponentKind;
  channel: number;
  name: string | null;
  enabled: boolean;
  // auto = apareció solo cuando la estación empezó a mandar ese dato.
  source: ComponentSource;
}

export const COMPONENT_LABEL: Record<ComponentKind, string> = {
  air: 'Temperatura y humedad',
  pressure: 'Barómetro',
  rain: 'Pluviómetro',
  wind: 'Viento',
  soil: 'Sonda de suelo',
  valve: 'Válvula de riego',
};

export const COMPONENT_HINT: Record<ComponentKind, string> = {
  air: 'Temperatura y humedad del aire',
  pressure: 'Presión atmosférica: anticipa cambios de tiempo',
  rain: 'Lluvia caída',
  wind: 'Velocidad y dirección',
  soil: 'Humedad del suelo (una por profundidad o lote)',
  valve: 'Un sector de riego por válvula',
};

export function componentDisplayName(c: Pick<DeviceComponent, 'kind' | 'channel' | 'name'>): string {
  if (c.name) return c.name;
  if (c.kind === 'soil') return `Sonda de suelo ${c.channel}`;
  if (c.kind === 'valve') return `Sector ${c.channel}`;
  return COMPONENT_LABEL[c.kind];
}

export function enabledOfKind<T extends Pick<DeviceComponent, 'kind' | 'enabled' | 'channel'>>(components: T[], kind: ComponentKind): T[] {
  return components.filter((c) => c.kind === kind && c.enabled).sort((a, b) => a.channel - b.channel);
}

export function hasComponent(components: Pick<DeviceComponent, 'kind' | 'enabled'>[], kind: ComponentKind): boolean {
  return components.some((c) => c.kind === kind && c.enabled);
}

// Resumen corto para listas: "Clima · Lluvia · Viento · 2 sondas · 1 válvula".
export function componentsSummary(components: Pick<DeviceComponent, 'kind' | 'enabled'>[]): string {
  const on = components.filter((c) => c.enabled);
  const count = (k: ComponentKind) => on.filter((c) => c.kind === k).length;
  const parts: string[] = [];
  if (count('air')) parts.push('Clima');
  if (count('pressure')) parts.push('Presión');
  if (count('rain')) parts.push('Lluvia');
  if (count('wind')) parts.push('Viento');
  const soil = count('soil');
  if (soil) parts.push(soil === 1 ? '1 sonda' : `${soil} sondas`);
  const valves = count('valve');
  if (valves) parts.push(valves === 1 ? '1 válvula' : `${valves} válvulas`);
  return parts.length > 0 ? parts.join(' · ') : 'Sin sensores todavía';
}

// Lo que se elige al dar de alta una estación: clima/barómetro/lluvia/viento
// sí o no, y cuántas sondas y válvulas.
export interface ComponentSelection {
  air: boolean;
  pressure: boolean;
  rain: boolean;
  wind: boolean;
  soil: number;
  valve: number;
}

export function selectionToSpecs(sel: ComponentSelection): { kind: ComponentKind; channel?: number }[] {
  const specs: { kind: ComponentKind; channel?: number }[] = [];
  if (sel.air) specs.push({ kind: 'air' });
  if (sel.pressure) specs.push({ kind: 'pressure' });
  if (sel.rain) specs.push({ kind: 'rain' });
  if (sel.wind) specs.push({ kind: 'wind' });
  for (let ch = 1; ch <= sel.soil; ch++) specs.push({ kind: 'soil', channel: ch });
  for (let ch = 1; ch <= sel.valve; ch++) specs.push({ kind: 'valve', channel: ch });
  return specs;
}
